import { Getters } from 'vuex-smart-module';
import { WriteupState } from '@/store/writeup/state';
import uniqBy from 'lodash/uniqBy';
import {
	IncentiveObject,
	protectionType,
	ScheduleTestDriveData,
	Timeslots,
	TimeslotsAppointmentData,
} from '@/types/writeup';

export class WriteupGetters extends Getters<WriteupState> {
	get writeupObject() {
		return this.state.writeupObject;
	}

	get termsObject() {
		return this.state.termsObject;
	}

	get incentives() {
		if (!this.state.incentives) return this.state.incentives;

		const incentives: IncentiveObject[] = [];

		this.state.incentives.forEach((incentive, item) => {
			if (incentive.terms.length > 1) {
				incentive.terms.forEach(term => {
					if (this.state.incentives) {
						incentives.push({ ...this.state.incentives[item] as IncentiveObject, ...{ terms: [term] } });
					}
				});
			} else {
				incentives.push(incentive);
			}
		});

		return incentives;
	}

	get vehicleVin() {
		return this.state.writeupObject?.vehicle?.vin;
	}

	get vehicleObject() {
		return this.state.writeupObject?.vehicle;
	}

	get pendingDealType() {
		return this.state.pendingDealType;
	}

	get dealType() {
		return this.getters.pendingDealType ?? this.getters.writeupObject?.type;
	}

	get isDealTypeUpdating() {
		return this.state.isDealTypeUpdating;
	}

	get isWriteupUpdating() {
		return this.state.isWriteupUpdating;
	}

	get isTermsLoading() {
		return this.state.isTermsLoading;
	}

	get isTermsUpdating() {
		return this.state.isTermsUpdating;
	}

	get isIncentivesSaving() {
		return this.state.isIncentivesSaving;
	}

	get isError() {
		return this.state.isError;
	}

	get tradeInObject() {
		return this.state.tradeInObject;
	}

	get rebates() {
		return this.state.rebates;
	}

	get isRebatesUpdating() {
		return this.state.isRebatesUpdating;
	}

	get isRebatesSettingsApplied() {
		return this.state.isRebatesSettingsApplied;
	}

	get lastIncentiveRequest() {
		return this.state.lastIncentiveRequest;
	}

	get vehicleList() {
		return this.state.vehicleList;
	}

	get isVehiclesFetching() {
		return this.state.isVehiclesFetching;
	}

	get isVehiclesDetailsFetching() {
		return this.state.isVehiclesDetailsFetching;
	}

	get vehicleSuggestionError() {
		return this.state.isVehicleSuggestionError;
	}

	get fiObjects() {
		return this.state.fiObjects;
	}

	get fiObjectSelectedList() {
		return this.state.fiObjects?.filter(fiObject => fiObject.selected === true);
	}

	get isFiObjectUpdating() {
		return this.state.isFiObjectUpdating;
	}

	get allCoveragesDropdownList() {
		return uniqBy(this.state.allCoverages, 'optionId');
	}

	get termList() {
		return this.state.termList;
	}

	get noImageUrl() { return 'https://cdn-ds.com/stock/w_220/h_163/noimage.jpg'; }

	get options() { return this.state.options; }

	get vehicleDetails() { return this.state.vehicleDetails; }

	get scheduleTestDriveData() { return this.state.scheduleTestDriveData; }

	get tradeInVehicleDetailsTitle() {
		return `${this.state.vehicleDetails?.year} ${this.state.vehicleDetails?.make} ${this.state.vehicleDetails?.model} ${this.state.vehicleDetails?.trim}`;
	}

	get datesAppointment() {
		return this.state.datesAppointment;
	}

	get timeslotsAppointment() {
		if (!this.state.timeslotsAppointment) return this.state.timeslotsAppointment;

		return this.state.timeslotsAppointment.timeslots.map(timeslotsItem => ({
			label: `${timeslotsItem.startTimeLabel} - ${timeslotsItem.endTimeLabel}`,
			startTime: timeslotsItem.startTime,
			endTime: timeslotsItem.endTime,
			startTimeLabel: timeslotsItem.startTimeLabel,
			endTimeLabel: timeslotsItem.endTimeLabel,
			disabled: timeslotsItem.isBusy,
		}));
	}

	get testDriveAppointmentData() {
		return this.state.testDriveAppointmentData;
	}

	get fIProtectionSelectedPlanCount() {
		const protectionList = this.state.fiObjects.filter(obj => obj.selected === true);

		return protectionList.length;
	}

	get fIProtectionPlanCount() {
		return this.state.fiObjects.length;
	}

	get tradeInEnterVehicleInputs() {
		return this.state.tradeInEnterVehicleInputs;
	}

	get tradeInReport() {
		return this.state.tradeInReport;
	}

	get isTradeInReportUpdating() {
		return this.state.isTradeInReportUpdating;
	}

	get optionProvider() {
		return this.state.optionProvider;
	}

	get appointmentId() {
		return this.state.appointmentResponseData?.appointmentId;
	}

	get isDatesAppointmentUpdating() {
		return this.state.isDatesAppointmentUpdating;
	}

	get isTimeslotsAppointmentUpdating() {
		return this.state.isTimeslotsAppointmentUpdating;
	}

	get paymentTermsData() {
		return this.state.paymentTermsDetails;
	}

	get isAccessoryUpdating() {
		return !this.state.writeupObject || this.state.isAccessoryUpdating;
	}

	get accessories() {
		return this.state.accessories;
	}

	get selectedAccessories() {
		return this.state.accessories?.filter(accessory => accessory.selected === true);
	}

	get accessoriesSelectedCount() {
		return this.selectedAccessories.length;
	}

	get taxesAndFees() {
		return this.state.taxesAndFees;
	}

	get shiftDigitalData() {
		return this.state.shiftDigitalData;
	}

	get gtmData() {
		return this.state.gtmData;
	}

	get tealiumData() {
		return this.state.tealiumData;
	}

	get ppLoadedState() {
		return this.state.isPpLoaded;
	}

	get isDealerDiscountFormLoading() {
		return this.state.isLoading;
	}

	get isBusy() {
		return this.state.isBusy;
	}
}
